// TermsOverTimeChart.js
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Chart from 'chart.js/auto';
import 'chartjs-adapter-date-fns';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { sk } from 'date-fns/locale';
import configApi from '../../config';

registerLocale('sk', sk);

const TermsOverTimeChart = () => {
    const chartRef = useRef(null);
    const [chartInstance, setChartInstance] = useState(null);
    const [startDate, setStartDate] = useState(new Date(new Date().setFullYear(new Date().getFullYear() - 1)));
    const [endDate, setEndDate] = useState(new Date());
    const token = localStorage.getItem("token");
    const config = {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };

    const fetchTerms = async () => {
        if (!startDate || !endDate) {
            console.warn('Začiatočný alebo koncový dátum nie je nastavený.');
            return;
        }

        if (startDate > endDate) {
            alert('Začiatočný dátum nemôže byť po koncovom dátume.');
            return;
        }

        try {
            const response = await axios.get(
                `${configApi.apiEndpoint}/api/Terms/GetTermsByDateRange`,
                {
                    ...config,
                    params: {
                        startDate: startDate.toISOString(),
                        endDate: endDate.toISOString(),
                    },
                }
            );
            const terms = response.data;
            processTerms(terms);
        } catch (error) {
            console.error('Chyba pri načítavaní termínov:', error);
        }
    };

    const processTerms = (terms) => {
        const termsByDate = {};
        let minDate = null;
        let maxDate = null;
        terms.forEach(term => {
            const dateObj = new Date(term.date);
            const date = dateObj.setHours(0, 0, 0, 0); // Normalize to midnight
            termsByDate[date] = (termsByDate[date] || 0) + 1;
            if (!minDate || date < minDate) minDate = date;
            if (!maxDate || date > maxDate) maxDate = date;
        });
        createChart(termsByDate, minDate, maxDate);
    };

    const createChart = (termsByDate, minDate, maxDate) => {
        const ctx = chartRef.current.getContext('2d');
        const gradient = ctx.createLinearGradient(0, 0, 0, 400);
        gradient.addColorStop(0, 'rgba(75, 192, 192, 0.4)');
        gradient.addColorStop(1, 'rgba(75, 192, 192, 0.0)');

        const dataPoints = Object.keys(termsByDate).sort().map(timestamp => ({
            x: new Date(parseInt(timestamp)),
            y: termsByDate[timestamp]
        }));

        const chartConfig = {
            type: 'line',
            data: {
                datasets: [{
                    label: 'Počet termínov',
                    data: dataPoints,
                    fill: true,
                    backgroundColor: gradient,
                    borderColor: '#4BC0C0',
                    borderWidth: 3,
                    tension: 0.4,
                    pointRadius: 3,
                    pointHoverRadius: 6,
                    pointBackgroundColor: '#4BC0C0',
                    pointBorderColor: '#fff',
                    pointBorderWidth: 2
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    x: {
                        type: 'time',
                        time: {
                            unit: 'day',
                            displayFormats: {
                                day: 'dd.MM.yyyy'
                            }
                        },
                        title: {
                            display: true,
                            text: 'Dátum'
                        },
                        min: minDate ? new Date(minDate) : undefined,
                        max: maxDate ? new Date(maxDate) : undefined,
                    },
                    y: {
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: 'Počet termínov'
                        }
                    }
                },
                plugins: {
                    title: {
                        display: true,
                        text: `Termíny v čase (${new Date(minDate).toLocaleDateString()} - ${new Date(maxDate).toLocaleDateString()})`
                    },
                    tooltip: {
                        callbacks: {
                            title: function (context) {
                                const date = context[0].parsed.x;
                                return new Date(date).toLocaleDateString();
                            }
                        }
                    }
                }
            }
        };

        if (chartRef.current) {
            const existingChart = Chart.getChart(chartRef.current);
            if (existingChart) {
                existingChart.destroy();
            }
        }

        const newChartInstance = new Chart(ctx, chartConfig);
        setChartInstance(newChartInstance);
    };

    useEffect(() => {
        fetchTerms();
    }, []);

    return (
        <div className="card-custom h-100">
            <div className="card-custom-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h5 className="m-0">Termíny v čase</h5>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <DatePicker
                        className="form-control form-control-sm rounded-pill text-center shadow-sm border-0"
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        dateFormat="dd.MM.yyyy"
                        placeholderText="Začiatok"
                        locale="sk"
                        calendarStartDay={1}
                    />
                    <span className="text-muted fw-bold">-</span>
                    <DatePicker
                        className="form-control form-control-sm rounded-pill text-center shadow-sm border-0"
                        selected={endDate}
                        onChange={(date) => setEndDate(date)}
                        dateFormat="dd.MM.yyyy"
                        placeholderText="Koniec"
                        locale="sk"
                        calendarStartDay={1}
                    />
                    <button className="btn btn-sm btn-outline-primary rounded-pill px-3 shadow-sm border-2 fw-bold" onClick={fetchTerms}>
                        Obnoviť
                    </button>
                </div>
            </div>
            <div style={{ height: '300px' }}>
                <canvas ref={chartRef}></canvas>
            </div>
        </div>
    );
};

export default TermsOverTimeChart;
